<template>
  <div class="page">
    <PageHeader
      ref="headerComponent"
      :items="menuItems"
      :route-item="routeMenuItem"
      :bannerNavigationLinks="bannerNavigationLinks"
      :cart="cart"
      :site-message="siteMessage"
      :banner-message="bannerMessage"
      :cart-count="cartCount"
      :favorites-count="favoritesCount"
      :show-contact-banner="showContactBanner"
      :show-search-bar="showSearchBar"
      :show-search-icon="showSearchIcon"
      :enable-search-suggestions="enableSearchSuggestions"
      :show-mobile-category-menu="showMobileCategoryMenu"
      :search-query="searchQuery"
      :country="countryCode"
      :scroll-container="scrollContainer"
      :cart-fetched="cartFetched"
      @chat="chat"
      @search="search"
      @openCart="openMiniCart"
      @checkout="checkout"
      @changeCountry="changeCountry({ country: $event })"
      @closeBanner="closeBanner"
      @openMenu="openMenu"
      @closeMenu="closeMenu"
    ></PageHeader>
    <div class="app-container-wrapper">
      <div class="app-container">
        <slot></slot>
      </div>
    </div>

    <Newsletter v-if="$route.meta.newsletter" />
    <Dialog />

    <CartSnackbar />

    <PageFooter></PageFooter>
  </div>
</template>
<script>
import { ref } from 'vue';
import { MediaUtils } from '@/pixel/index';
import CountryUtils from '@/components/commons/CountryUtils.js';
import PageHeader from '@/components/header/PageHeader.vue';
import PageFooter from '@/components/footer/PageFooter.vue';
import useFavorite from '@/compositions/useFavorite.js';
import useCommon from '@/compositions/useCommon';
import { onMounted, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import Dialog from '@/components/commons/Dialog/Dialog.vue';
import useResize from '@/compositions/useResize';

export default {
  components: {
    PageHeader,
    PageFooter,
    Newsletter: defineAsyncComponent(() => import('@/components/commons/Newsletter.vue')),
    Dialog,
    CartSnackbar: defineAsyncComponent(() => import('@/components/commons/CartSnackbar.vue')),
  },

  async setup() {
    const router = useRouter();
    const { favoritesCount } = useFavorite();
    const { isTabletAndSmaller } = useResize();


    const {
      countryId,
      menuItems,
      bannerNavigationLinks,
      showContactBanner,
      showMobileCategoryMenu,
      searchQuery,
      enableSearchSuggestions,
      cartCount,
      cart,
      siteMessage,
      notifyCartItemsAdded,
      cartFetched,
      bannerMessage,
      routeMenuItem,
      chat,
      search,
      openMiniCart,
      checkout,
      changeCountry,
      closeBanner,
      updateNotifyCartItemsAdded,
      fetchData,
      getUser,
      loadMessaging,
      getSessionData,
      refreshUserData,
      refreshCartData,
      updateIntialized,
      updateCurrentRoute: updateStoreCurrentRoute,
    } = useCommon(router);

    onMounted(() => {
      getUser();
      getSessionData();
      loadMessaging();

      updateIntialized(true);

      window.addEventListener('storage', (event) => {
        try {
          const { key } = event;
          if (key === 'article-user') {
            refreshUserData();
          } else if (key === 'article-cart') {
            refreshCartData();
          }
        } catch {}
      });

      window.setTimeout(async () => {
        if (process.env.ENV === 'staging' || (!process.env.DEBUG && process.env.CONFIG_SENTRY_RELEASE)) {
          const Chat = (await import('@/components/commons/Chat.js')).default;
          Chat.init();
        }
      }, 0);
    });

    await fetchData();

    function updateCurrentRoute() {
      const {
        name, //
        path,
        params,
        query,
        meta,
      } = router.currentRoute.value;
      const acceptableQueries = meta.acceptableQueries || [];
      const newQuery = {};

      if (acceptableQueries.length && Object.keys(query).length) {
        acceptableQueries.forEach((value) => {
          if (value in query) {
            newQuery[value] = query[value];
          }
        });
      }

      updateStoreCurrentRoute({
        name,
        path,
        params,
        query: newQuery,
        meta,
      });
    }

    if (import.meta.env.SSR) {
      updateCurrentRoute();
    }

    return {
      favoritesCount,
      bannerNavigationLinks,
      countryId,
      menuItems,
      showContactBanner,
      showMobileCategoryMenu,
      searchQuery,
      enableSearchSuggestions,
      cartCount,
      cart,
      siteMessage,
      notifyCartItemsAdded,
      cartFetched,
      bannerMessage,
      routeMenuItem,
      chat,
      search,
      openMiniCart,
      checkout,
      changeCountry,
      closeBanner,
      updateNotifyCartItemsAdded,
      updateCurrentRoute,
      isTabletAndSmaller
    };
  },

  data() {
    return {
      scrollContainer: false,
      mounted: false,
    };
  },

  computed: {
    countryCode() {
      return CountryUtils.getCountryById(this.countryId).code;
    },

    showSearchBar() {
      const openSearchBarPages = ['/search', '/help-center'];
      const closedSearchBarPages = ['/product', '/cart', '/trade', '/business'];
      return (
        openSearchBarPages.some((page) => this.$route.path.indexOf(page) > -1) ||
        (!closedSearchBarPages.some((page) => this.$route.path.indexOf(page) > -1) && this.isTabletAndSmaller)
      );    
    },

    showSearchIcon() {
      const openSearchIconPages = ['/help-center'];
      const enableSearchIconToggle = openSearchIconPages.some((page) => this.$route.path.indexOf(page) > -1);
      return !this.showSearchBar || enableSearchIconToggle; 
    },
  },

  watch: {
    notifyCartItemsAdded(newVal) {
      if (newVal && this.$refs && this.$refs.headerComponent) {
        this.$refs.headerComponent.notifyCartItemsAdded();
        this.updateNotifyCartItemsAdded(false);
      }
    },
    $route: {
      handler() {
        this.updateCurrentRoute();
      },
      immediate: true,
    },
  },

  mounted() {
    this.mounted = true;
  },

  methods: {
    openMenu() {
      if (MediaUtils.isMobileAndSmaller) {
        const html = document.querySelector('html');
        html.classList.add('overflow-hidden');
        html.classList.add('overflow-hidden-long');

        this.scrollContainer = true;
      }
    },

    closeMenu() {
      const html = document.querySelector('html');
      html.classList.remove('overflow-hidden');
      html.classList.remove('overflow-hidden-long');
      this.scrollContainer = false;
    },
  },
};
</script>
<style lang="postcss" scoped>
.page {
  display: flex;
  flex-flow: column;
  min-height: 100vh;
  max-width: 2560px;
  width: 100%;
  margin: 0 auto;

  .app-container-wrapper {
    display: flex;
    flex-flow: column;
    flex-grow: 2;

    .app-container {
      flex-grow: 2;
    }
  }
}
</style>
